import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { TEST_IDS } from 'test-ids'
import { detectPlatform, type Platform } from 'components/map/three-d/platform'

/**
 * How to drive the 3D view.
 *
 * The gestures are Cesium's own defaults (ScreenSpaceCameraController: left
 * drag to move, wheel/right drag/pinch to zoom, middle drag or Ctrl+drag to
 * tilt, Shift+drag to look), with tilt additionally bound to Option on macOS —
 * see components/map/three-d/platform. They are split by platform because the
 * differences are not cosmetic: a Mac trackpad has no middle button, and touch
 * has no modifier keys at all.
 */

const PLATFORMS: Platform[] = ['windows', 'mac', 'touch']

const ACTIONS = ['pan', 'zoom', 'tilt', 'look'] as const

export default function ThreeDHelpDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  // Opens on the tab for this device, and shares its detection with the camera
  // bindings so the gesture shown is the gesture bound.
  const [platform, setPlatform] = useState<Platform>(detectPlatform)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={TEST_IDS.threeDHelpDialog}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}
      >
        <span style={{ flex: 1 }}>{t('three_d_help_title')}</span>
        <IconButton
          onClick={onClose}
          aria-label={t('close')}
          data-testid={TEST_IDS.threeDHelpDialogClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={platform}
          onChange={(_event, value) => setPlatform(value as Platform)}
          aria-label={t('three_d_help_title')}
          data-testid={TEST_IDS.threeDHelpPlatformTabs}
        >
          {PLATFORMS.map((name) => (
            <Tab key={name} value={name} label={t(`three_d_help_${name}`)} />
          ))}
        </Tabs>

        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('three_d_help_action')}</TableCell>
              <TableCell>{t('three_d_help_gesture')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ACTIONS.map((action) => (
              <TableRow key={action}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t(`three_d_help_${action}`)}
                </TableCell>
                <TableCell>{t(`three_d_help_${platform}_${action}`)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          {t('three_d_help_settings_title')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>{t('three_d_surface')}</strong>{' '}
          {t('three_d_help_surface_body')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>{t('vertical_exaggeration')}</strong>{' '}
          {t('three_d_help_exaggeration_body')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          {t('three_d_help_limits_title')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('three_d_help_limits_body')}
        </Typography>
      </DialogContent>
    </Dialog>
  )
}
